import React, { useState, useEffect } from 'react';
import { Trophy, Medal, Crown, TrendingUp, Filter, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const Compete = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [userRank, setUserRank] = useState(null);
  const [totalPlayers, setTotalPlayers] = useState(0);
  const [timeframe, setTimeframe] = useState('all_time');
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadLeaderboard();
  }, [timeframe]);

  const loadLeaderboard = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/platform/leaderboard?timeframe=${timeframe}&limit=50`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        setLeaderboard(data.leaderboard);
        setUserRank(data.user_rank);
        setTotalPlayers(data.total_players);
      }
    } catch (error) {
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-6 h-6 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-zinc-300" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-orange-600" />;
    return <span className="text-sm font-bold text-foreground/40">#{rank}</span>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0B0F14] via-[#121923] to-[#0B0F14] py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-heading font-bold mb-2 flex items-center justify-center gap-3">
            <Trophy className="w-10 h-10 text-[#C9A227]" />
            Leaderboard
          </h1>
          <p className="text-foreground/60">Compete with players worldwide</p>
        </div>

        {/* Your Rank Card */}
        {userRank && (
          <div className="bg-gradient-to-r from-[#C9A227]/20 to-[#E1C25B]/10 border-2 border-[#C9A227] rounded-xl p-6 mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-[#C9A227] flex items-center justify-center">
                  <TrendingUp className="w-8 h-8 text-[#0B0F14]" />
                </div>
                <div>
                  <p className="text-sm text-foreground/60">Your Current Rank</p>
                  <p className="text-3xl font-heading font-bold text-[#C9A227]">#{userRank}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-foreground/60">Out of</p>
                <p className="text-2xl font-bold">{totalPlayers} Players</p>
              </div>
            </div>
          </div>
        )}

        {/* Timeframe Filter */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {['daily', 'weekly', 'all_time'].map((tf) => (
            <button
              key={tf}
              onClick={() => setTimeframe(tf)}
              data-testid={`filter-${tf}`}
              className={`
                px-4 py-2 rounded-lg font-semibold text-sm transition-all
                ${timeframe === tf
                  ? 'bg-[#C9A227] text-[#0B0F14]'
                  : 'bg-[#1A2432] text-foreground/70 hover:text-foreground hover:bg-[#1A2432]/70'
                }
              `}
            >
              {tf === 'all_time' ? 'All Time' : tf.charAt(0).toUpperCase() + tf.slice(1)}
            </button>
          ))}
        </div>

        {/* Leaderboard Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#C9A227]" />
          </div>
        ) : (
          <div className="bg-[#121923] border border-white/10 rounded-xl overflow-hidden">
            {leaderboard.length === 0 ? (
              <div className="text-center py-12 text-foreground/40">
                <Trophy className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p>No rankings yet. Start playing to appear on the leaderboard!</p>
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {leaderboard.map((entry) => (
                  <div
                    key={entry.user_id}
                    data-testid={`leaderboard-rank-${entry.rank}`}
                    className={`
                      flex items-center justify-between p-4
                      ${entry.rank <= 3 ? 'bg-gradient-to-r from-[#C9A227]/5 to-transparent' : ''}
                    `}
                  >
                    <div className="flex items-center gap-4">
                      {/* Rank */}
                      <div className="w-12 flex items-center justify-center">
                        {getRankIcon(entry.rank)}
                      </div>

                      {/* Avatar */}
                      <img
                        src={entry.picture || `https://api.dicebear.com/7.x/avataaars/svg?seed=${entry.user_id}`}
                        alt={entry.name}
                        className="w-10 h-10 rounded-full border-2 border-[#C9A227]/30"
                      />

                      {/* Name */}
                      <div>
                        <p className="font-semibold">{entry.name}</p>
                        <p className="text-xs text-foreground/60">Level {entry.level}</p>
                      </div>
                    </div>

                    {/* XP */}
                    <div className="text-right">
                      <p className="text-xl font-bold text-[#C9A227]">{entry.xp.toLocaleString()}</p>
                      <p className="text-xs text-foreground/60">XP</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Compete;